import { useEffect } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { PAYABLE_PLANS, whatsappLink } from "../content/site";
import { PayButton } from "../components/PayButton";
import { Reveal } from "../components/Reveal";

export default function Pay() {
  useEffect(() => {
    document.title = "Pay Online — WebStudios";
    window.scrollTo(0, 0);
    return () => { document.title = "WebStudios — Website Design, Hosting & Maintenance UK"; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-700">
      <header className="border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <a href="/" data-testid="pay-logo" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="WebStudios — website design, hosting and maintenance" className="h-11 w-auto object-contain" />
          </a>
          <a href="/" data-testid="pay-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="pay-page">
        <Reveal>
          <p className="font-mono2 text-xs tracking-[0.25em] uppercase text-[#0055FF] mb-4">Secure online payment</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900">Pay for your plan online.</h1>
          <p className="mt-5 max-w-xl text-base text-slate-600 leading-relaxed">
            Choose your plan below to pay securely by card through Stripe. Monthly plans are subscriptions you can cancel anytime — your service starts as soon as we've confirmed your website details.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PAYABLE_PLANS.map((p, i) => (
            <Reveal key={p.lookup} delay={0.05 * i}>
              <article data-testid={`pay-plan-${p.lookup}`}
                className="h-full bg-white border border-slate-200 soft-shadow rounded-2xl p-6 flex flex-col transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#0055FF]/40 hover:shadow-[0_16px_48px_rgba(2,6,23,0.08)]">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-lg font-semibold text-slate-900 tracking-tight">{p.name}</h2>
                  {p.tag && <span className="shrink-0 text-[10px] font-mono2 uppercase tracking-widest text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1">{p.tag}</span>}
                </div>
                <p className="mt-3 font-mono2 text-3xl font-semibold text-slate-900">{p.price}<span className="text-sm text-slate-400 font-normal">{p.per}</span></p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.desc}</p>
                <PayButton lookupKey={p.lookup} label={`Pay ${p.price}${p.per}`} testid={`pay-button-${p.lookup}`}
                  className="mt-6 w-full bg-[#0055FF] hover:bg-[#0044CC] text-white glow-blue" />
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-14 grid sm:grid-cols-3 gap-4">
            {[
              { t: "1 · Choose your plan", d: "Pick the care plan or hosting option that fits your website. Monthly plans are rolling subscriptions — there is no contract and you can cancel anytime by telling us." },
              { t: "2 · Pay securely by card", d: "You'll be taken to Stripe's secure checkout to pay by debit or credit card. WebStudios never sees, stores or processes your card details — everything is handled by Stripe's bank-grade security." },
              { t: "3 · We set everything up", d: "As soon as your payment is confirmed, we get in touch to connect your plan to your website. Hosting includes SSL, backups and uptime monitoring from day one." },
            ].map((s) => (
              <div key={s.t} className="bg-[#FAFAFA] border border-slate-200 rounded-2xl p-6">
                <h3 className="font-display text-base font-semibold text-slate-900 tracking-tight">{s.t}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 bg-[#FAFAFA] border border-slate-200 rounded-2xl p-6 sm:p-8">
            <h3 className="font-display text-lg font-semibold text-slate-900 tracking-tight">Good to know</h3>
            <div className="mt-3 space-y-3 text-sm text-slate-600 leading-relaxed">
              <p>All plans on this page are fixed-price monthly or annual subscriptions. Larger website projects — new builds, redesigns, e-commerce stores and booking systems — are quoted individually; request a free quote and we'll give you a clear written price before any work begins.</p>
              <p>Monthly subscriptions can be cancelled at any time with notice — just message us on WhatsApp or email us and we'll stop future payments. Annual hosting covers a full year of managed hosting and works out two months cheaper than paying monthly.</p>
              <p>If anything goes wrong with a payment, or you're charged in error, contact us straight away and we'll put it right. You can reach us on WhatsApp at 07440 752224 or by email at webstudios.co.uk@gmail.com.</p>
              <p><strong className="text-slate-900">When does my plan start?</strong> Your subscription begins the moment your payment is confirmed, and we'll contact you to connect it to your website. If you're paying for hosting for a site we're still building, your hosting simply activates at launch — you won't lose days.</p>
              <p><strong className="text-slate-900">What cards are accepted?</strong> Stripe accepts all major debit and credit cards. Your card statement will show the payment clearly, and you'll receive a receipt by email for every payment.</p>
              <p><strong className="text-slate-900">Can I switch plans later?</strong> Yes — you can move between Care, Care Pro and Care Premium as your needs change. Just message us and we'll adjust your subscription from the next billing date.</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white border border-slate-200 soft-shadow rounded-2xl p-5">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              Payments are processed securely by Stripe — we never see or store your card details.
              Need a custom project instead? <a href="/#contact" data-testid="pay-quote-link" className="text-[#0055FF] hover:underline">Request a free quote</a>
              {" "}or <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="pay-whatsapp-link" className="text-[#25D366] hover:underline">message us on WhatsApp</a>.
            </p>
          </div>
        </Reveal>
      </main>
    </div>
  );
}
